import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSettings, useUpdateSettings } from '@/api/settings';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField, Input } from '@/components/ui/FormField';
import { US_STATES } from '@/features/clients/usStates';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
/*
 * Configuration → Practice → Practice Information. Combines billing-provider
 * identity (NPI, EIN, taxonomy, address — feeds the 837P pre-flight
 * validator) with operational practice info (email, timezone) that the rest
 * of the app reads. Calendar display hours live in PracticeHoursPanel under
 * Scheduling.
 *
 * Persists into settings.practice via PATCH /api/settings/practice. The
 * server does a shallow merge, so we only send the keys managed here.
 */

const TIMEZONES: readonly { value: string; label: string }[] = [
    { value: 'America/New_York',    label: 'Eastern Time (ET)' },
    { value: 'America/Chicago',     label: 'Central Time (CT)' },
    { value: 'America/Denver',      label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage',   label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu',    label: 'Hawaii Time (HT)' },
];

interface FormState {
    practiceName: string;
    npi: string;
    ein: string;
    taxonomy: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    timezone: string;
    // Client-enforced idle auto-logout in minutes. Lives in the security
    // namespace (consumed by useIdleTimeoutMinutes); surfaced here because
    // it's the only PMA-side session control left after the Cognito cutover.
    sessionTimeout: string;
}

const EMPTY: FormState = {
    practiceName: '', npi: '', ein: '', taxonomy: '',
    phone: '', email: '', address: '', city: '', state: '', zip: '',
    timezone: 'America/New_York',
    sessionTimeout: '15',
};

export function PracticeInformationPanel() {
    const { data, isLoading, isError } = useSettings();
    const updateM = useUpdateSettings();
    const updatePractice = useAuthStore((s) => s.updatePractice);

    const [form, setForm] = useState<FormState>(EMPTY);
    const [err, setErr] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (!data) return;
        const p = data.practice ?? {};
        const s = data.security ?? {};
        setForm({
            practiceName: p.practiceName ?? '',
            npi:          p.npi ?? '',
            ein:          p.ein ?? '',
            taxonomy:     p.taxonomy ?? '',
            phone:        p.phone ?? '',
            email:        p.email ?? '',
            address:      p.address ?? '',
            city:         p.city ?? '',
            state:        p.state ?? '',
            zip:          p.zip ?? '',
            timezone:     p.timezone ?? 'America/New_York',
            sessionTimeout: typeof s.sessionTimeout === 'number' ? String(s.sessionTimeout) : '15',
        });
    }, [data]);

    function set<K extends keyof FormState>(k: K, v: FormState[K]) {
        setSaved(false);
        setErr(null);
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);
        setSaved(false);
        if (form.npi && !/^\d{10}$/.test(form.npi)) {
            setErr('NPI must be 10 digits.');
            return;
        }
        if (form.ein && !/^\d{9}$/.test(form.ein.replace(/-/g, ''))) {
            setErr('EIN must be 9 digits.');
            return;
        }
        if (form.taxonomy && !/^[A-Z0-9]{10}$/i.test(form.taxonomy)) {
            setErr('Taxonomy must be a 10-character NUCC code.');
            return;
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setErr('Enter a valid email address.');
            return;
        }
        const idle = Number(form.sessionTimeout);
        if (!Number.isInteger(idle) || idle < 1 || idle > 120) {
            setErr('Idle timeout must be 1–120 minutes.');
            return;
        }
        try {
            await updateM.mutateAsync({
                type: 'practice',
                updates: {
                    practiceName: form.practiceName.trim(),
                    npi: form.npi.trim(),
                    ein: form.ein.replace(/-/g, '').trim(),
                    taxonomy: form.taxonomy.trim().toUpperCase(),
                    phone: form.phone.trim(),
                    email: form.email.trim(),
                    address: form.address.trim(),
                    city: form.city.trim(),
                    state: form.state.trim().toUpperCase(),
                    zip: form.zip.trim(),
                    timezone: form.timezone,
                },
            });
            await updateM.mutateAsync({
                type: 'security',
                updates: { sessionTimeout: idle },
            });
            // Server propagated to practices.name; mirror into the auth store
            // so the topbar updates without a logout/login round trip.
            updatePractice({ name: form.practiceName.trim() });
            setSaved(true);
        } catch (e2) {
            setErr(extractErrorMessage(e2, 'Could not save.'));
        }
    }

    if (isLoading) {
        return (
            <div className="p-12 flex items-center justify-center text-text-subtle">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (isError) {
        return <div className="p-12 text-center text-error">Couldn't load settings.</div>;
    }

    return (
        <div>
            <div className="mb-1">
                <h2 className="font-display text-lg font-bold text-text">Practice Information</h2>
            </div>
            <p className="text-xs text-text-subtle mb-3">
                Billing-provider identity used on electronic claims (837P Loop 2010AA) plus
                the contact and locale preferences the rest of the app reads. NPI, EIN, and
                taxonomy are required before a claim can be submitted.
            </p>

            <Card className="p-5 max-w-3xl">
                <form onSubmit={handleSubmit}>
                    <FormField label="Practice name" required>
                        {(id) => (
                            <Input
                                id={id}
                                value={form.practiceName}
                                onChange={(e) => set('practiceName', e.target.value)}
                            />
                        )}
                    </FormField>

                    <div className="grid grid-cols-3 gap-3">
                        <FormField label="NPI" hint="10-digit National Provider Identifier">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.npi}
                                    onChange={(e) => set('npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    inputMode="numeric"
                                    placeholder="1234567890"
                                />
                            )}
                        </FormField>
                        <FormField label="EIN" hint="9-digit Employer Identification Number">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.ein}
                                    onChange={(e) => set('ein', e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
                                    inputMode="numeric"
                                    placeholder="12-3456789"
                                />
                            )}
                        </FormField>
                        <FormField label="Taxonomy" hint="NUCC code (e.g. 103K00000X for BCBA)">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.taxonomy}
                                    onChange={(e) => set('taxonomy', e.target.value.toUpperCase().slice(0, 10))}
                                    placeholder="103K00000X"
                                    className="font-mono"
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Phone">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.phone}
                                    onChange={(e) => set('phone', e.target.value)}
                                    placeholder="(555) 123-4567"
                                />
                            )}
                        </FormField>
                        <FormField label="Email" hint="Reply-to address on outbound system emails">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="email"
                                    autoComplete="off"
                                    value={form.email}
                                    onChange={(e) => set('email', e.target.value)}
                                    placeholder="admin@example.com"
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="grid grid-cols-[2fr,1.2fr,90px,110px] gap-3">
                        <FormField label="Address">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.address}
                                    onChange={(e) => set('address', e.target.value)}
                                    placeholder="123 Main St, Suite 100"
                                />
                            )}
                        </FormField>
                        <FormField label="City">
                            {(id) => (
                                <Input id={id} value={form.city} onChange={(e) => set('city', e.target.value)} />
                            )}
                        </FormField>
                        <FormField label="State">
                            {(id) => (
                                <select
                                    id={id}
                                    value={form.state}
                                    onChange={(e) => set('state', e.target.value)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                                >
                                    <option value="">—</option>
                                    {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}
                                </select>
                            )}
                        </FormField>
                        <FormField label="ZIP">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.zip}
                                    onChange={(e) => set('zip', e.target.value)}
                                    placeholder="12345"
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="border-t border-border mt-2 mb-4" />

                    <FormField label="Timezone" hint="Drives session reminder times and audit timestamps">
                        {(id) => (
                            <select
                                id={id}
                                value={form.timezone}
                                onChange={(e) => set('timezone', e.target.value)}
                                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none"
                            >
                                {TIMEZONES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        )}
                    </FormField>

                    <FormField
                        label="Idle timeout (minutes)"
                        hint="Auto-logout after no user activity. Password policy, MFA, and absolute session lifetime are handled by sign-in (Cognito)."
                    >
                        {(id) => (
                            <Input
                                id={id}
                                value={form.sessionTimeout}
                                onChange={(e) => set('sessionTimeout', e.target.value.replace(/\D/g, '').slice(0, 3))}
                                inputMode="numeric"
                                placeholder="15"
                            />
                        )}
                    </FormField>

                    {err && <p className="text-sm text-error mb-2">{err}</p>}
                    {saved && <p className="text-sm text-success mb-2">Saved.</p>}

                    <div className="flex justify-end">
                        <Button variant="primary" size="sm" type="submit" disabled={updateM.isPending}>
                            {updateM.isPending ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
